import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const IntegratedComponentsHub: React.FC = () => {
  const { t } = useTranslation('landing');

  return (
    <section data-capabilities-section className="py-20 bg-ink-50">
      <div className="max-w-5xl mx-auto px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="text-xs font-semibold tracking-widest uppercase text-[#E85D3D] mb-3">
            The CLEAR Platform
          </div>
          <h2 className="font-display text-2xl font-semibold text-ink-900">
            {t('capabilities.title', 'Four integrated components, one unified ecosystem')}
          </h2>
        </motion.div>

        {/* Hub Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="relative w-full max-w-[760px] mx-auto"
          style={{ aspectRatio: '1.3' }}
        >
          {/* Connection Lines */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full pointer-events-none">
            {/* Top line */}
            <div className="absolute w-[2px] bg-[#E8E8E6] left-1/2 -translate-x-1/2" style={{ top: '100px', height: 'calc(50% - 130px)' }}>
              <motion.div
                className="absolute w-[6px] h-[6px] bg-[#E85D3D] -left-[2px]"
                animate={{ top: ['0%', '100%'], opacity: [0, 1, 1, 0] }}
                transition={{ duration: 2.5, ease: 'easeInOut', repeat: Infinity }}
              />
            </div>
            {/* Bottom line */}
            <div className="absolute w-[2px] bg-[#E8E8E6] left-1/2 -translate-x-1/2" style={{ bottom: '100px', height: 'calc(50% - 130px)' }}>
              <motion.div
                className="absolute w-[6px] h-[6px] bg-[#E85D3D] -left-[2px]"
                animate={{ bottom: ['0%', '100%'], opacity: [0, 1, 1, 0] }}
                transition={{ duration: 2.5, ease: 'easeInOut', repeat: Infinity, delay: 0.6 }}
              />
            </div>
            {/* Left line */}
            <div className="absolute h-[2px] bg-[#E8E8E6] top-1/2 -translate-y-1/2" style={{ left: '140px', width: 'calc(50% - 160px)' }}>
              <motion.div
                className="absolute w-[6px] h-[6px] bg-[#E85D3D] -top-[2px]"
                animate={{ left: ['0%', '100%'], opacity: [0, 1, 1, 0] }}
                transition={{ duration: 2.5, ease: 'easeInOut', repeat: Infinity, delay: 0.3 }}
              />
            </div>
            {/* Right line */}
            <div className="absolute h-[2px] bg-[#E8E8E6] top-1/2 -translate-y-1/2" style={{ right: '140px', width: 'calc(50% - 160px)' }}>
              <motion.div
                className="absolute w-[6px] h-[6px] bg-[#E85D3D] -top-[2px]"
                animate={{ right: ['0%', '100%'], opacity: [0, 1, 1, 0] }}
                transition={{ duration: 2.5, ease: 'easeInOut', repeat: Infinity, delay: 0.9 }}
              />
            </div>
          </div>

          {/* Center Hub */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120px] h-[120px] bg-gradient-to-br from-[#E85D3D] to-[#D14D2D] flex flex-col items-center justify-center text-white text-center z-10" style={{ boxShadow: '0 8px 32px rgba(232, 93, 61, 0.3)' }}>
            <svg className="w-7 h-7 mb-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
            </svg>
            <div className="text-[10px] font-semibold tracking-wide uppercase leading-tight">
              Unified<br/>Ecosystem
            </div>
          </div>

          {/* Crisis Detection - Top */}
          <ComponentCard
            position="top"
            iconColor="bg-[#FEF3F0] text-[#E85D3D]"
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.348 14.651a3.75 3.75 0 010-5.303m5.304 0a3.75 3.75 0 010 5.303m-7.425 2.122a6.75 6.75 0 010-9.546m9.546 0a6.75 6.75 0 010 9.546M5.106 18.894c-3.808-3.808-3.808-9.98 0-13.789m13.788 0c3.808 3.808 3.808 9.981 0 13.79M12 12h.008v.007H12V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
              </svg>
            }
            name="Crisis Detection"
            description="AI-powered early warning"
            tags={['Social Media', 'Secondary Data', 'Satellite']}
          />

          {/* Situation Analysis - Left */}
          <ComponentCard
            position="left"
            iconColor="bg-[#FFF8F0] text-[#E8923D]"
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
            }
            name="Situation Analysis"
            description="Synthesized intelligence"
            tags={['Field Surveys', 'Crisis Mapping']}
          />

          {/* Field Console - Right */}
          <ComponentCard
            position="right"
            iconColor="bg-[#F0F7F4] text-[#2D7A5F]"
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
              </svg>
            }
            name="Field Console"
            description="Mobile-first dashboard"
            tags={['Community Data', 'Field Reports']}
          />

          {/* Rapid Cash - Bottom */}
          <ComponentCard
            position="bottom"
            iconColor="bg-[#FEF7F0] text-[#D97B35]"
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            name="Rapid Cash"
            description="AI-powered cash assistance"
            tags={['Payment Systems', 'Household Data']}
          />
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-8 pt-6 border-t border-[#E8E8E6]"
        >
          <p className="text-sm text-ink-600">
            From fragmented tools to <strong className="text-[#E85D3D] font-semibold">seamless integration</strong>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

interface ComponentCardProps {
  position: 'top' | 'left' | 'right' | 'bottom';
  iconColor: string;
  icon: React.ReactNode;
  name: string;
  description: string;
  tags: string[];
}

const ComponentCard: React.FC<ComponentCardProps> = ({ position, iconColor, icon, name, description, tags }) => {
  const positionClasses = {
    top: 'top-[-10px] left-1/2 -translate-x-1/2',
    left: 'top-1/2 left-[-20px] -translate-y-1/2',
    right: 'top-1/2 right-[-20px] -translate-y-1/2',
    bottom: 'bottom-[-10px] left-1/2 -translate-x-1/2'
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      whileInView={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      viewport={{ once: true }}
      className={`absolute flex flex-col items-center text-center bg-white p-4 pt-5 w-[160px] shadow-sm border border-[#F0F0EE] ${positionClasses[position]}`}
    >
      <div className={`w-12 h-12 flex items-center justify-center mb-2 ${iconColor}`}>
        <div className="w-[22px] h-[22px]">{icon}</div>
      </div>
      <div className="text-xs font-semibold text-ink-900 mb-1 leading-tight">{name}</div>
      <div className="text-[10px] text-ink-500 mb-3 leading-tight">{description}</div>
      <div className="flex flex-wrap gap-1 justify-center">
        {tags.map((tag, i) => (
          <span key={i} className="flex items-center gap-1 px-2 py-1 bg-[#F8F8F6] text-[9px] font-medium text-ink-600">
            {tag}
          </span>
        ))}
      </div>
    </motion.div>
  );
};

export default IntegratedComponentsHub;
